import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Box, Typography, Card, Grid, TextField, Button, Divider,
  Switch, FormControlLabel, Alert, Snackbar, CircularProgress,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { API_BASE_URL } from "../../config";
import { minRequestedEnd } from "../../utils/contractExtension";

function Section({ title, children, id }) {
  return (
    <Card id={id} elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff", mb: 3 }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>{title}</Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Card>
  );
}

export default function ClientSettingsPage() {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState("");
  const [error, setError] = useState("");

  // Local editable settings (in production, these would be persisted via /api/client/settings)
  const [sessionMins, setSessionMins] = useState(60);
  const [mfa, setMfa] = useState(false);
  const [forceReset, setForceReset] = useState(true);

  // Notification prefs
  const [notifyExpiry, setNotifyExpiry] = useState(true);
  const [notifySeat, setNotifySeat] = useState(true);

  // Extension request
  const [requestedEnd, setRequestedEnd] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/client/stats`)
      .then((res) => {
        setStats(res.data);
        const min = minRequestedEnd(res.data?.contract_end);
        setRequestedEnd(min);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading && location.state?.focusContract) {
      document.getElementById("contract-extension")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, location.state]);

  const handleRequestExtension = async () => {
    setError("");
    if (!requestedEnd) {
      setError("Please choose a requested contract end date.");
      return;
    }
    setSubmittingRequest(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/client/licence/extension-request`, {
        requested_contract_end: requestedEnd,
        notes: requestNotes.trim() || undefined,
      });
      setSnack(res.data?.message || "Extension request sent to platform admin");
      setRequestNotes("");
      const statsRes = await axios.get(`${API_BASE_URL}/client/stats`);
      setStats(statsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit extension request");
    } finally {
      setSubmittingRequest(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  const loginUrl = `${window.location.origin}?realm=${stats?.slug || ""}`;
  const contractEndDisplay = stats?.contract_end
    ? new Date(stats.contract_end).toLocaleDateString()
    : "—";
  const requestMin = minRequestedEnd(stats?.contract_end);
  const extReq = stats?.extension_request;
  const extStatus = extReq?.status || "none";

  const extensionStatusAlert = (() => {
    if (extStatus === "pending") {
      const reqDate = extReq?.requested_contract_end
        ? new Date(extReq.requested_contract_end).toLocaleDateString()
        : "—";
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Extension request pending — requested end date {reqDate}. Platform admin must approve via Extend Licence.
        </Alert>
      );
    }
    if (extStatus === "approved") {
      const approvedDate = extReq?.approved_contract_end
        ? new Date(extReq.approved_contract_end).toLocaleDateString()
        : contractEndDisplay;
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          Extension approved — contract end is now {approvedDate}.
        </Alert>
      );
    }
    return null;
  })();

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Organisation authentication and notification preferences
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {/* Org info (read-only) */}
      <Section title="Organisation">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Organisation Name"
              value={stats?.org_name || ""} InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-input": { bgcolor: "#f9fafb" } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Tier"
              value={stats?.tier || ""} InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-input": { bgcolor: "#f9fafb" } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Status"
              value={stats?.status || ""} InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-input": { bgcolor: "#f9fafb" } }}
            />
          </Grid>
        </Grid>
        <Box mt={2}>
          <Typography fontSize="12px" color="text.secondary" mb={0.5}>
            Organisation Login URL
          </Typography>
          <Typography
            fontSize="13px"
            fontFamily="monospace"
            sx={{
              bgcolor: "#f9fafb", border: "1px solid #e8e8e8", borderRadius: 1,
              px: 1.5, py: 1, wordBreak: "break-all", color: "#0094FB",
            }}
          >
            {loginUrl}
          </Typography>
        </Box>
      </Section>

      {/* Contract extension request */}
      <Section id="contract-extension" title="Contract">
        <Typography fontSize="13px" color="text.secondary" mb={2}>
          Request a contract extension. Platform admin will receive an email and must approve it via Extend Licence.
          You can submit as many requests as you need.
        </Typography>
        {extensionStatusAlert}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Current contract end"
              value={contractEndDisplay} InputProps={{ readOnly: true }}
              sx={{ "& .MuiInputBase-input": { bgcolor: "#f9fafb" } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Requested contract end" type="date"
              value={requestedEnd}
              onChange={(e) => setRequestedEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: requestMin }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small" fullWidth label="Notes (optional)"
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              placeholder="Reason for extension…"
            />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            startIcon={submittingRequest ? <CircularProgress size={16} color="inherit" /> : <ExtensionOutlinedIcon />}
            disabled={submittingRequest || !requestedEnd}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={handleRequestExtension}
          >
            {submittingRequest ? "Submitting…" : "Request Extend Contract"}
          </Button>
        </Box>
      </Section>

      {/* Authentication */}
      <Section title="Authentication">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small" fullWidth label="Session Timeout (minutes)" type="number"
              value={sessionMins}
              onChange={(e) => setSessionMins(+e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={mfa} onChange={(e) => setMfa(e.target.checked)} />}
              label="Require MFA"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={forceReset} onChange={(e) => setForceReset(e.target.checked)} />}
              label="Force password reset on first login"
            />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={() => setSnack("Authentication settings saved")}
          >
            Save Auth Settings
          </Button>
        </Box>
      </Section>

      {/* Notifications */}
      <Section title="Notifications">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={notifyExpiry} onChange={(e) => setNotifyExpiry(e.target.checked)} />}
              label="Licence expiry reminders"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={notifySeat} onChange={(e) => setNotifySeat(e.target.checked)} />}
              label="Seat usage warnings"
            />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained" size="small" startIcon={<SaveOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
            onClick={() => setSnack("Notification preferences saved")}
          >
            Save Notifications
          </Button>
        </Box>
      </Section>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
